import { useState, useEffect, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import { FiChevronDown, FiArrowRight, FiExternalLink } from "react-icons/fi";
import { useCountUp } from "../hooks/useCountUp";

const hidden = (delay: number): CSSProperties => ({
  opacity: 0,
  transform: "translateY(16px)",
  transition: "opacity 0.6s ease-out, transform 0.6s ease-out",
  transitionDelay: `${delay}ms`,
});

const shown = (delay: number): CSSProperties => ({
  opacity: 1,
  transform: "translateY(0)",
  transition: "opacity 0.6s ease-out, transform 0.6s ease-out",
  transitionDelay: `${delay}ms`,
});

const stats = [
  { value: 4, suffix: "", label: "Projects", animate: true },
  { value: 40, suffix: "+", label: "Brain Notes", animate: true },
  { value: 1, suffix: "", label: "Certified (CKAD)", animate: true },
  { value: 0, suffix: "DevSecOps + AI", label: "Core Focus", animate: false },
];

const Hero = () => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const projectCount = useCountUp(4, 1400, mounted);
  const noteCount = useCountUp(40, 1800, mounted);
  const certCount = useCountUp(1, 1600, mounted);
  const s = (d: number) => (mounted ? shown(d) : hidden(d));

  return (
    <section className="relative overflow-hidden pt-20 pb-12 md:pt-28 md:pb-16">
      {/* Subtle ambient glow */}
      <div
        className="absolute top-0 right-0 w-[500px] h-[500px] opacity-20 dark:opacity-10 pointer-events-none"
        aria-hidden="true"
        style={{
          background: "radial-gradient(circle, rgba(245,158,11,0.15) 0%, transparent 70%)",
          transform: "translate(30%, -30%)",
        }}
      />

      <div className="relative max-w-6xl mx-auto px-6 md:px-10">
        <div className="flex flex-col lg:flex-row lg:items-start lg:gap-12">
          <div className="max-w-3xl flex-1">
            {/* Label */}
            <div style={s(0)} className="mb-6">
              <span className="inline-flex items-center gap-2 rounded-full border border-accent/20 bg-accent-dim px-3 py-1 text-xs font-semibold uppercase tracking-wider text-accent">
                <span className="h-1.5 w-1.5 rounded-full bg-accent animate-pulse" />
                DevSecOps Lead & AI Enthusiast
              </span>
            </div>

            {/* Headline */}
            <h1
              style={s(120)}
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-light-text dark:text-text-primary leading-[1.1] tracking-tight"
            >
              Building infrastructure
              <br />
              at the intersection of{" "}
              <span className="text-accent">systems</span> and{" "}
              <span className="text-accent">intelligence</span>.
            </h1>

            {/* Subhead */}
            <p
              style={s(220)}
              className="font-mono text-sm tracking-wide text-light-muted dark:text-text-muted mb-8"
            >
              kubernetes · security · observability · ai
            </p>

            {/* Description */}
            <p
              style={s(300)}
              className="text-base md:text-lg leading-relaxed max-w-2xl text-light-text-secondary dark:text-text-secondary mb-10"
            >
              CKAD certified. CKA & CKS in progress. I architect secure cloud
              infrastructure, Kubernetes platforms, and AI-augmented workflows.
              Everything I learn gets synthesized into my{" "}
              <a
                href="https://brain.rakeshkadam.me"
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent hover:text-accent-muted transition-colors underline underline-offset-2 decoration-accent/30 hover:decoration-accent"
              >
                Digital Brain
              </a>
              {" "}- a living knowledge base that compounds over time.
            </p>

            {/* CTAs */}
            <div style={s(400)} className="flex flex-wrap items-center gap-3 mb-14">
              <Link
                to="/contact"
                className="group inline-flex items-center gap-2 px-6 py-3 bg-light-text dark:bg-text-primary text-light-surface dark:text-surface-0 rounded-lg font-semibold text-sm hover:bg-light-text-secondary dark:hover:bg-text-secondary transition-colors"
              >
                Contact Me
                <FiArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
              </Link>
              <a
                href="https://brain.rakeshkadam.me"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 px-6 py-3 border border-light-border-strong dark:border-border-subtle text-light-text dark:text-text-primary rounded-lg font-semibold text-sm hover:border-accent/40 hover:text-accent dark:hover:text-accent transition-colors"
              >
                Explore Brain
                <FiExternalLink size={14} className="opacity-60 group-hover:opacity-100" />
              </a>
            </div>

            {/* Stats */}
            <div style={s(500)} className="flex flex-wrap gap-3">
              {stats.map((stat, i) => (
                <div
                  key={stat.label}
                  className="flex items-center gap-2.5 rounded-lg border border-light-border dark:border-border-subtle bg-light-surface dark:bg-surface-1 px-4 py-2.5"
                  style={{
                    animation: mounted
                      ? `fadeIn 0.4s ease-out ${600 + i * 80}ms both`
                      : "none",
                  }}
                >
                  <span className="font-mono text-sm font-semibold text-accent">
                    {stat.animate
                      ? `${stat.label === "Projects" ? projectCount : stat.label === "Brain Notes" ? noteCount : certCount}${stat.suffix}`
                      : stat.suffix}
                  </span>
                  <span className="text-xs text-light-muted dark:text-text-muted">
                    {stat.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Profile photo / avatar */}
          <div style={s(400)} className="hidden lg:flex shrink-0 items-start justify-center pt-4">
            <div className="relative">
              <div className="h-64 w-64 rounded-full overflow-hidden border-2 border-light-border dark:border-border-subtle shadow-card dark:shadow-glow">
                <img
                  src="/rakesh.png"
                  alt="Rakesh Kadam"
                  className="h-full w-full object-cover"
                  draggable={false}
                />
              </div>
              <div className="absolute -bottom-3 -right-3 rounded-lg border border-light-border bg-light-surface px-3 py-1.5 text-xs font-semibold dark:border-border-subtle dark:bg-surface-1 shadow-card">
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-success animate-pulse" />
                  Open to work
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="mt-12 flex justify-center">
          <button
            onClick={() =>
              document
                .getElementById("projects-cta")
                ?.scrollIntoView({ behavior: "smooth" })
            }
            aria-hidden="true"
            tabIndex={-1}
            className="animate-bounce-gentle text-light-muted/40 dark:text-text-faint/40 hover:text-accent dark:hover:text-accent transition-colors"
          >
            <FiChevronDown size={20} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Hero;
